import { useLayoutEffect, useRef } from "react";

const HALF_SIDE = 8 * Math.sqrt(3);
const LOGO = "M24 16V32L40 40V8L24 16ZM24 16L8 8V32";
const ease = (t: number) => {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
};

/// The mycorr logo (`M24 16V32L40 40V8L24 16ZM24 16L8 8V32`) at `progress` 0;
/// by 1 its wing has turned over onto the right half like a page, lifting
/// toward the viewer; by 2 the centre line has kinked out into an equilateral
/// triangle pointing left, whose slanted edges still pass through (24,16) and (24,32).
export function railGlyphPath(progress: number): string {
  const fold = ease(progress);
  const kink = ease(progress - 1);
  const apex = 24 - HALF_SIDE * kink;
  const base = 40 - (16 - HALF_SIDE) * kink;
  const body = `M24 16L${apex} 24L24 32L${base} 40L${base} 8Z`;
  // Folded, the wing lies on the body's edges, and drawing it twice thickens them.
  if (fold === 1) return body;
  const lift = 110 / (110 - 16 * Math.sin(Math.PI * fold));
  const x = 24 - 16 * Math.cos(Math.PI * fold) * lift;
  return `${body}M24 16L${x} ${24 - 16 * lift}L${x} ${24 + 8 * lift}`;
}

/// The mycorr logo at `progress` 0, a house at 1. The centre line swings flat
/// to the left about its foot into the floor, then the V across the top pops
/// up into the roof.
export function homeGlyphPath(progress: number): string {
  const floor = ease(progress / 0.6);
  const roof = ease((progress - 0.4) / 0.6);
  if (floor === 0) return LOGO;
  const eave = 8 + 12 * roof;
  const top = `L8 ${eave}L24 ${16 - 8 * roof}L40 ${eave}L40 40`;
  // Landed, the floor's far end is the wall's foot, and only closing joins that corner.
  if (floor === 1) return `M8 40${top}Z`;
  const base = 32 + 8 * floor;
  const swing = (Math.PI / 2) * floor;
  const tipX = 24 - 16 * Math.sin(swing);
  return `M8 ${base}${top}L24 ${base}L${tipX} ${base - 16 * Math.cos(swing)}`;
}

/// Steps a glyph toward `goal` a frame at a time, writing the path itself so
/// the morph never re-renders its owner.
export function useGlyphMorph(
  goal: number,
  pathAt: (progress: number) => string,
  msPerStep: number,
) {
  const pathRef = useRef<SVGPathElement>(null);
  const progress = useRef(goal);
  useLayoutEffect(() => {
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let frame = 0;
    let last = performance.now();
    const step = (now: number) => {
      const room = reduce ? Infinity : (now - last) / msPerStep;
      const gap = goal - progress.current;
      progress.current += Math.sign(gap) * Math.min(Math.abs(gap), room);
      last = now;
      pathRef.current?.setAttribute("d", pathAt(progress.current));
      if (progress.current !== goal) frame = requestAnimationFrame(step);
    };
    step(last);
    return () => cancelAnimationFrame(frame);
  }, [goal, pathAt, msPerStep]);
  return pathRef;
}
